import { PageWithAsideLayout } from '@/components/client-layouts/page-with-aside-layout';
import ArchivedNonMobileHeader from '@/components/deal-from-router/_components/archived-non-mobile-header.tsx';
import DealFileDragAndDrop from '@/components/deal-from-router/_components/deal-file-drag-and-drop';
import MobileHeader from '@/components/deal-from-router/_components/mobile-header';
import MobileSubheader from '@/components/deal-from-router/_components/mobile-subheader';
import NonMobileAside from '@/components/deal-from-router/_components/non-mobile-aside';
import NonMobileHeader from '@/components/deal-from-router/_components/non-mobile-header';
import NonMobileMain from '@/components/deal-from-router/_components/non-mobile-main';
import InboxLayout from '@/components/inbox-from-router/_components/inbox-layout';
import { PAGE_ROUTES } from '@/components/nav/utils/url-builders.ts';
import FullscreenMessage from '@/components/ui/layout/fullscreen-message';
import LayoutBox1024 from '@/components/ui/layout/layout-1024-box';
import { AISuggestionsProvider } from '@/contexts/ai-suggestions-context';
import { useAuthContext } from '@/contexts/auth-context';
import { useDealContext } from '@/contexts/deal-provider';
import { useHydrationContext } from '@/contexts/hydration/hydration-provider';
import { useInboxContext } from '@/contexts/inbox-provider';
import { useArchivedDealQuery } from '@/hooks/fetch-store-and-stitch/use-archived-deals.ts';
import { useDealByShortId } from '@/hooks/use-deals-selectors';
import {
  FEATURE_FLAG_NAMES,
  useIsFeatureEnabled,
} from '@/hooks/use-feature-flags.ts';
import useRedirectToInformativeDealUrl from '@/hooks/use-redirect-to-informative-deal-url';
import { dealStore } from '@/models/stores';
import { createFileRoute, useLocation } from '@tanstack/react-router';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

export const Route = createFileRoute('/(authenticated)/$hub/deal/$shortId/')({
  component: DealPageWrapper,
  staticData: {
    keepAlive: true,
  },
});

dayjs.extend(relativeTime);

export const DealPage = observer(() => {
  const { hasValidSession } = useAuthContext();
  const location = useLocation();

  const [showCoregistrant] = useState(false);

  const { currentShortId: shortId } = useDealContext();
  const deal = useDealByShortId(shortId);

  const { redirectToInformativeDealUrl } = useRedirectToInformativeDealUrl();

  const { isFeatureEnabled: isArchiveEnabled } = useIsFeatureEnabled({
    featureFlagName: FEATURE_FLAG_NAMES.ARCHIVE_DEAL,
  });
  const { isAPIHydrating } = useHydrationContext();

  const archivedEligible =
    !!shortId && hasValidSession && !deal && !isAPIHydrating;

  const archivedDealQuery = useArchivedDealQuery({
    shortId: shortId as string,
    enabled: archivedEligible,
  });

  useEffect(() => {
    if (archivedDealQuery.data) {
      dealStore.upsertArchivedDeal(archivedDealQuery.data);
    }
  }, [archivedDealQuery.data]);

  useEffect(() => {
    // UX NOTE: Upon loading the deal page, we want to redirect to the
    // informative deal url one time
    // and we want to preserve the query params so the focus UX works

    // DEV NOTE: this useEffect can run during component unmounting
    // (especially when navigating to another component that causes mobx deal store updates)
    // so the guard clause exists to prevent redirectToInformativeDealUrl from firing
    // when navigating away from the deal page.
    if (!location.pathname.includes(`/${PAGE_ROUTES.DEAL}/`)) {
      return;
    }

    if (!deal) {
      return;
    }

    if (!deal.primary_customer || !deal.vehicle_listing) {
      return;
    }

    if (deal.short_id === shortId) {
      return;
    }

    redirectToInformativeDealUrl({
      primaryCustomerFullName: deal.primary_customer.full_name,
      vehicle: deal.vehicle_listing,
      primarySalesRep: deal.primary_sales_rep,
      secondarySalesRep: deal?.secondary_sales_rep,
      shortId: deal.short_id,
    });
  }, [deal, location.pathname]);

  if (!hasValidSession) {
    return (
      <FullscreenMessage>
        <div className="text-2xl">Your session has expired</div>
      </FullscreenMessage>
    );
  }

  if (!deal && archivedDealQuery.isFetching) {
    return (
      <FullscreenMessage>
        <div className="text-2xl">
          {archivedDealQuery.isFetching
            ? 'Fetching Archived Deal...'
            : 'Deal Loading...'}
        </div>
      </FullscreenMessage>
    );
  }

  if (isAPIHydrating && !deal) {
    return (
      <FullscreenMessage>
        <div className="text-2xl">
          {/* UX note: intentionally blank to reduce cognitive load */}
        </div>
      </FullscreenMessage>
    );
  }

  if (!deal) {
    return (
      <FullscreenMessage>
        <div className="text-2xl">No Deal Found</div>
      </FullscreenMessage>
    );
  }

  if (deal && deal.archived_at && !isArchiveEnabled) {
    return (
      <FullscreenMessage>
        <div className="text-2xl">
          This deal has been archived. You don't have access to it.
        </div>
      </FullscreenMessage>
    );
  }

  return (
    <DealFileDragAndDrop deal={deal}>
      <AISuggestionsProvider>
        <div className="h-full">
          <PageWithAsideLayout
            // DEV NOTE: the key is used to force a re-render of the children components
            // If we don't do this, the inputs of the children components and confirmation dots will bug out
            // it'll think the values are different and sent a bunch of unnecessary API requests to update everything
            // Thus when the deal is updated, we remount all the children components to allow it to reset the values and confirmation dots
            key={`page-with-aside-layout-${deal.id}`}
            mobile={{
              header: <MobileHeader deal={deal} />,
              subheader: <MobileSubheader deal={deal} />,
              aside: <NonMobileAside deal={deal} />,
              main: (
                <NonMobileMain
                  deal={deal}
                  showCoregistrant={showCoregistrant}
                />
              ),
            }}
            nonMobile={{
              header: !!deal.archived_at ? (
                <ArchivedNonMobileHeader deal={deal} />
              ) : (
                <NonMobileHeader deal={deal} />
              ),
              aside: <NonMobileAside deal={deal} />,
              main: (
                <LayoutBox1024>
                  <NonMobileMain
                    deal={deal}
                    showCoregistrant={showCoregistrant}
                  />
                </LayoutBox1024>
              ),
            }}
          />
        </div>
      </AISuggestionsProvider>
    </DealFileDragAndDrop>
  );
});

export function DealPageWrapper() {
  const { isInboxView } = useInboxContext();

  if (isInboxView) {
    return <InboxLayout aside={<DealPage />} />;
  }

  return <DealPage />;
}
